import api from "./api";
import { headers as configHeaders } from "./config";

const isDev = process.env.NODE_ENV === "development";
const log = (...args: unknown[]) => isDev && console.log(...args);
const logError = (...args: unknown[]) => isDev && console.error(...args);

export interface ServiceError {
	error: true;
	status?: number | null;
	data?: unknown;
}

export interface ServiceSuccess<T> {
	status: number;
	data: T;
}

function buildErrorResponse(err: unknown): ServiceError {
	const axiosErr = err as { response?: { status?: number; data?: unknown } };
	return {
		error: true,
		status: axiosErr?.response?.status ?? null,
		data: axiosErr?.response?.data ?? null,
	};
}

/**
 * Classe base para services da API.
 * T = tipo retornado pelo formatDetailData (detail/slug)
 * TList = tipo retornado pelo formatListData (listagens). Default = T.
 */
export default class BaseService<T = any, TList = T> {
	public resource = "";

	constructor(resource: string) {
		this.resource = resource;
	}

	formatDetailData(data: unknown): T {
		return data as T;
	}

	formatListData(data: unknown): TList {
		return data as TList;
	}

	async list(isCommentsList?: boolean): Promise<any> {
		const hasQueryParam = this.resource.includes("=");
		const suffix = isCommentsList ? "-v2" : "";
		const slash = hasQueryParam ? "" : "/";
		const resourceURL = `${this.resource}${suffix}${slash}`;

		try {
			const response = await api({ method: "GET", url: resourceURL });
			return response.data;
		} catch (err) {
			logError(`[BS] Erro ao listar ${this.resource}:`, err);
			return { error: true };
		}
	}

	async loadPage(page: number, timestamp?: string): Promise<any> {
		const resourceURL = timestamp
			? `${this.resource}/?page=${page}&data=${timestamp}`
			: `${this.resource}/?page=${page}`;

		try {
			const response = await api.get(resourceURL);
			return response.data;
		} catch (err) {
			logError(`[BS] Erro ao carregar página ${page} - ${this.resource}:`, err);
			return { error: true };
		}
	}

	async get_by_category_slug(slug: string | undefined): Promise<T | ServiceError> {
		try {
			const response = await api.get(`${this.resource}/categoria/${slug}`);
			return this.formatDetailData(response.data);
		} catch (err) {
			logError(`[BS] Erro ao listar ${this.resource}:`, err);
			return { error: true };
		}
	}

	async get_by_slug(slug: string | undefined): Promise<T | ServiceError> {
		try {
			const response = await api.get(`${this.resource}/${slug}/`);
			return this.formatDetailData(response.data.results ? response.data.results : response.data);
		} catch (err) {
			logError(`[BS] Erro ao listar ${this.resource}:`, err);
			return { error: true };
		}
	}

	async get_by_id(id: number | string | undefined): Promise<any> {
		try {
			const response = await api.get(`${this.resource}/${id}/`);
			log("[BS] GET BY ID ==> ", response);
			return response.data;
		} catch (err) {
			logError(`[BS] Erro ao buscar ${this.resource}:`, err);
			return { error: true };
		}
	}

	async create(data: unknown, headers = configHeaders): Promise<any> {
		try {
			const response = await api.post(`${this.resource}/`, data, headers);
			return { status: response.status, data: response.data };
		} catch (err) {
			logError(`[BS] Erro ao criar ${this.resource}:`, err);
			return { error: true };
		}
	}

	async save(id: number | string, data: unknown, headers = configHeaders): Promise<any> {
		try {
			const response = await api.put(`${this.resource}/${id}/`, data, headers);
			return { status: response.status, data: response.data };
		} catch (err) {
			logError(`[BS] Erro ao salvar ${this.resource}:`, err);
			return { error: true };
		}
	}

	async update(id: number | string | null, data: unknown, headers = configHeaders): Promise<any> {
		try {
			const response = await api.put(`${this.resource}/${id}/`, data, headers);
			return { status: response.status, data: response.data };
		} catch (err) {
			logError(`[BS] Erro ao atualizar ${this.resource}:`, err);
			return buildErrorResponse(err);
		}
	}

	async delete(id: number | string): Promise<T | ServiceError> {
		try {
			const response = await api.delete(`${this.resource}/${id}/`);
			return this.formatDetailData(response.data);
		} catch (err) {
			logError(`[BS] Erro ao deletar ${this.resource}:`, err);
			return buildErrorResponse(err);
		}
	}

	async deleteComment(commentId: number | string): Promise<any> {
		try {
			const response = await api.delete(`${this.resource}/${commentId}/`);
			return response.data;
		} catch (err) {
			logError(`[BS] Erro ao deletar comentário ${this.resource}:`, err);
			return buildErrorResponse(err);
		}
	}

	async like(id: number | string): Promise<any> {
		try {
			const response = await api.post(`${this.resource}/like/`, {
				"entidade_pk": id,
			});
			return response.data;
		} catch (err) {
			logError(`[BS] Erro ao curtir ${this.resource}:`, err);
			return buildErrorResponse(err);
		}
	}
}
